import React, { useCallback, useEffect } from 'react';

import { DrawerOverlay } from '@chakra-ui/react';
import {
  checkOwnersAndCollaboratorsModified,
  checkTagsModified,
} from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/ChangeDetails/ChangeDetails';
import { getKeyResultApi } from 'components/RabbitOKRSidePanel/datahooks';
import { closeSecondaryPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useUnsavedDataAlert from 'components/hooks/useUnsavedDataAlert';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { closeCreateOKRPanel, useCreateObjectivePanel } from 'reducers/createObjectivePanelSlice';
import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { okrCreationPanelStateSelector, okrInitializedStateSelector } from 'selectors/okrCreationPanelSelector';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { generateKey } from 'constants/helper';

import { getEmployeeDetails } from 'services/employee';

import { BottomDrawer } from 'uiToolkitV1/BottomDrawer';

import { initializeOKRCreationPanelState } from './OKRCreationPanelSlice';
import Layout from './PrimaryPanel/V2/Layout';

export const checkCreationDetailsModified = (localStateValues, initialDetails, customTags = []) => {
  const {
    text,
    description,
    timeBoundDetails,
    parentPost,
    measure,
    measureType,
    startingProgressValue,
    target,
    progressType,
    startTS,
    dueTS,
    visibilityType,
    objectiveLevel,
  } = initialDetails || {};

  const isDetailsChanged =
    (localStateValues?.text !== undefined && localStateValues?.text !== text) ||
    (localStateValues?.measure !== undefined && localStateValues?.measure !== measure) ||
    (localStateValues?.measureType !== undefined && localStateValues?.measureType !== measureType) ||
    (localStateValues?.startingProgressValue !== undefined &&
      +localStateValues.startingProgressValue !== startingProgressValue) ||
    (localStateValues?.target !== undefined && +localStateValues.target !== target) ||
    (localStateValues?.timeBoundDetails !== undefined &&
      localStateValues?.timeBoundDetails?.timelineTag !== timeBoundDetails?.timelineTag) ||
    (localStateValues?.parentPost?.uuid ?? null) !== (parentPost?.uuid ?? null) ||
    checkOwnersAndCollaboratorsModified(initialDetails?.toIds, localStateValues?.toIds) ||
    (localStateValues?.startTS !== undefined && !moment(localStateValues?.startTS).isSame(startTS, 'date')) ||
    (localStateValues?.dueTS !== undefined && !moment(localStateValues?.dueTS).isSame(dueTS, 'date')) ||
    (localStateValues?.visibilityType !== undefined && localStateValues?.visibilityType !== visibilityType) ||
    (localStateValues?.description !== undefined && localStateValues?.description !== description) ||
    checkTagsModified(customTags, localStateValues?.tagKeyList) ||
    (localStateValues?.progressType !== undefined && localStateValues?.progressType !== progressType) ||
    (localStateValues?.objectiveLevel !== undefined && localStateValues?.objectiveLevel !== objectiveLevel);

  return isDetailsChanged;
};

const OKRCreationPanel = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();
  const okrDetails = useSelector(okrCreationPanelStateSelector);
  const okrInitializedState = useSelector(okrInitializedStateSelector);
  const isUnsavedChangesInOKR = checkCreationDetailsModified(okrDetails, okrInitializedState);
  const {
    isOpen,
    onCancel,
    selectedTimeBoundPeriod,
    isAlignmentAtParentLevel,
    selectedObjective = null,
    okrPanelType = OKR_TYPES.OBJECTIVE,
    ownerId: ownerIdFromURL,
    parentId: parentIdFromURL,
    onObjectiveCreation,
    onKeyResultCreation,
    parentMeasure,
    parentMeasureType,
    parentProgressType,
    ownerEmployee: preSelectedOwnerEmployee,
    okrPanelPayload,
  } = useCreateObjectivePanel();

  const { data: timelineData } = useTimeBoundTimePeriods();

  const { current } = timelineData ?? {};

  const timePeriodToUse = selectedTimeBoundPeriod ?? current;
  const handleDiscardAndCancel = useCallback(() => {
    dispatch(closeSecondaryPanel());
    dispatch(closeCreateOKRPanel({ history, location }));
    if (typeof onCancel === 'function') {
      onCancel();
    }
  }, [dispatch, history, location, onCancel]);

  const handleOnCancel = () => {
    if (isUnsavedChangesInOKR) {
      showUnsavedDataAlertDialog({
        onCancelCallback: () => {
          handleDiscardAndCancel();
        },
      });
    } else {
      handleDiscardAndCancel();
    }
  };

  const { details } = useSelector(kRDrawerSecondaryPanelSelector);

  /* Initialize OKRCreationPanelState */

  useEffect(() => {
    const parentPost = selectedObjective?.uuid ? selectedObjective : null;
    if (isOpen) {
      const payload = {
        timeBoundDetails: timePeriodToUse,
        parentPost,
        type: okrPanelType,
        uiKey: generateKey(7),
        parentMeasure,
        parentMeasureType,
        parentProgressType,
      };

      if (okrPanelPayload?.from === 'REVIEW_ACTION_PLAN') {
        payload.text = okrPanelPayload?.title ?? '';
      }

      try {
        const parentPromise = parentIdFromURL ? getKeyResultApi({ id: parentIdFromURL }) : Promise.resolve(parentPost);
        const ownerPromise = ownerIdFromURL
          ? getEmployeeDetails(ownerIdFromURL)
          : Promise.resolve(preSelectedOwnerEmployee || {});
        if (timePeriodToUse) {
          Promise.allSettled([parentPromise, ownerPromise]).then((values) => {
            const [parentObject, ownerObject] = values;
            const ownerEmployee = ownerObject.value;
            if (ownerObject.status === 'fulfilled' && ownerEmployee?.id) {
              const ownerObj = {
                email: ownerEmployee?.email,
                id: ownerEmployee?.id,
                displayName: ownerEmployee?.displayName,
                profilePhoto: ownerEmployee?.profilePhotoSrc,
                designation: ownerEmployee?.designation,
                employeeFunction: ownerEmployee?.employeeFunction,
                ownerType: 'OWNER',
              };
              payload.toIds = [ownerObj];
            }
            if (parentObject.status === 'fulfilled' && parentIdFromURL) {
              payload.parentPost = parentObject?.value?.entity;
            }

            dispatch(initializeOKRCreationPanelState(payload));
          });
        } else {
          dispatch(initializeOKRCreationPanelState(payload));
        }
      } catch (error) {
        dispatch(initializeOKRCreationPanelState(payload));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isOpen,
    timePeriodToUse,
    isAlignmentAtParentLevel,
    selectedObjective,
    okrPanelType,
    parentMeasure,
    parentMeasureType,
    parentProgressType,
    ownerIdFromURL,
    parentIdFromURL,
  ]);

  return (
    <BottomDrawer isOpen={isOpen} placement="bottom" onClose={handleOnCancel}>
      <DrawerOverlay />

      <Layout
        onClose={handleOnCancel}
        onKeyResultCreation={onKeyResultCreation}
        onObjectiveCreation={onObjectiveCreation}
        isOpenAsParent={details?.parentState}
      />
    </BottomDrawer>
  );
};

export default OKRCreationPanel;
